// pages/ShippingPolicy.jsx
import React, {useState, useEffect, useLayoutEffect} from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const ShippingPolicy = () => {
    const [isScrolled, setIsScrolled] = useState(false);


    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const shippingOptions = [
        {
            name: "Standard Shipping",
            time: "5-7 business days",
            cost: "$8.95",
            description: "Tracked delivery with signature confirmation",
            regions: ["Continental US"],
            icon: "📦",
            color: "bg-muted-sage/10"
        },
        {
            name: "Express Shipping",
            time: "2-3 business days",
            cost: "$14.95",
            description: "Priority processing and delivery",
            regions: ["Continental US"],
            icon: "🚀",
            color: "bg-champagne-gold/10"
        },
        {
            name: "International Standard",
            time: "10-14 business days",
            cost: "From $24.95",
            description: "Duties and taxes calculated at checkout",
            regions: ["Global"],
            icon: "🌍",
            color: "bg-muted-sage/10"
        },
        {
            name: "Luxury Overnight",
            time: "Next business day",
            cost: "$29.95",
            description: "Guaranteed delivery by 5PM",
            regions: ["Select US cities"],
            icon: "✨",
            color: "bg-champagne-gold/10"
        }
    ];

    const timelineSteps = [
        { day: "Day 1-2", label: "Order Processed", icon: "📦" },
        { day: "Day 2-3", label: "Shipped", icon: "🚚" },
        { day: "Day 5-7", label: "In Transit", icon: "✈️" },
        { day: "Day 7+", label: "Delivered", icon: "🏠" }
    ];

    const sections = [
        {
            title: "Our Shipping Commitment",
            content: [
                "We deliver KROMAIC products with the same care and attention that goes into creating them.",
                "Each order is meticulously packaged to ensure your products arrive in perfect condition, ready for your beauty ritual."
            ]
        },
        {
            title: "Processing Time",
            content: [
                "Orders are processed within 1-2 business days, Monday through Friday, excluding holidays.",
                "Orders placed after 2PM EST are processed the next business day.",
                "During peak seasons (November-January), processing may take an additional 1-2 days."
            ]
        },
        {
            title: "Shipping Methods & Costs",
            content: [
                "We partner with premium carriers to ensure secure and reliable delivery.",
                "Shipping costs vary based on destination, weight, and selected service.",
                "Free standard shipping is available for orders over $75 in the continental US."
            ]
        },
        {
            title: "International Shipping",
            content: [
                "We ship to over 50 countries worldwide.",
                "International customers are responsible for customs duties, taxes, and fees, which are calculated at checkout.",
                "Shipping times vary by destination and customs processing.",
                "Please ensure your shipping address is complete and accurate to avoid delays."
            ]
        },
        {
            title: "Order Tracking",
            content: [
                "You will receive a shipping confirmation email with tracking information once your order ships.",
                "Track your order directly through the carrier's website using your tracking number.",
                "For international orders, tracking may be limited once the package leaves the US."
            ]
        },
        {
            title: "Delivery Issues",
            content: [
                "If your package shows as delivered but you haven't received it, first check with household members and neighbors.",
                "Contact the carrier directly with your tracking number for investigation.",
                "If the carrier confirms loss or damage, contact our support team within 7 days of expected delivery."
            ]
        },
        {
            title: "Address Accuracy",
            content: [
                "Please double-check your shipping address before completing your order.",
                "We cannot modify shipping addresses once an order is processed.",
                "Packages returned due to incorrect addresses are subject to return shipping fees."
            ]
        },
        {
            title: "Holiday Schedule",
            content: [
                "We do not process or ship orders on major US holidays.",
                "During holiday periods, please allow additional processing and delivery time.",
                "Upcoming holiday closures are posted on our website and social media channels."
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-pearl-white">
            {/* Fixed Navbar - EXACTLY like Shop page */}
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    isScrolled
                        ? 'bg-white/95 backdrop-blur-sm shadow-soft'
                        : 'bg-transparent'
                }`}
            >
                <Navbar />
            </div>

            {/* Hero Section */}
            <section id="hero" className="pt-20">
                <div className="relative min-h-[40vh] flex items-center bg-gradient-to-br from-soft-ivory via-pearl-white to-nude-beige overflow-hidden">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                        className="container-luxury relative z-10"
                    >
                        <div className="max-w-4xl">
                            <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl text-deep-charcoal mb-6 leading-tight">
                                Shipping Policy
                            </h1>
                            <p className="font-poppins text-xl text-charcoal-60 max-w-2xl">
                                Careful delivery of your beauty ritual
                            </p>
                        </div>
                    </motion.div>

                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-1/3 h-full opacity-5">
                        <div className="absolute top-1/4 right-0 w-64 h-64 rounded-full bg-champagne-gold blur-3xl" />
                    </div>
                </div>
            </section>

            {/* Shipping Options */}
            <section id="shipping-options" className="section-spacing">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="mb-16"
                    >
                        <h2 className="font-playfair text-4xl md:text-5xl text-deep-charcoal text-center mb-12">
                            Shipping Options
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {shippingOptions.map((option, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    whileHover={{ y: -8 }}
                                    className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-lift transition-all duration-300"
                                >
                                    <div className={`${option.color} w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-4`}>
                                        {option.icon}
                                    </div>

                                    <h3 className="font-cormorant text-xl text-deep-charcoal mb-2">
                                        {option.name}
                                    </h3>

                                    <div className="font-playfair text-2xl text-muted-sage mb-3">
                                        {option.cost}
                                    </div>

                                    <div className="font-poppins text-sm text-champagne-gold mb-4">
                                        {option.time}
                                    </div>

                                    <p className="font-poppins text-sm text-charcoal-60 mb-4">
                                        {option.description}
                                    </p>

                                    <div className="text-xs text-charcoal-40">
                                        Available in: {option.regions.join(", ")}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Free Shipping Banner */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="mb-12"
                    >
                        <div className="bg-gradient-to-r from-muted-sage/10 to-champagne-gold/10 rounded-2xl p-8 text-center">
                            <h3 className="font-cormorant text-2xl text-deep-charcoal mb-4">
                                Free Standard Shipping
                            </h3>
                            <p className="font-poppins text-charcoal-60">
                                Available for all orders over $75 within the continental US
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Policy Content */}
            <section id="policy-content" className="section-spacing bg-soft-ivory">
                <div className="container-luxury">
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12">
                            {sections.map((section, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className={`py-8 ${index < sections.length - 1 ? 'border-b border-nude-beige' : ''}`}
                                >
                                    <h2 className="font-cormorant text-2xl md:text-3xl text-deep-charcoal mb-6">
                                        {section.title}
                                    </h2>
                                    <div className="space-y-4">
                                        {section.content.map((paragraph, pIndex) => (
                                            <motion.p
                                                key={pIndex}
                                                initial={{ opacity: 0 }}
                                                whileInView={{ opacity: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.5, delay: index * 0.1 + pIndex * 0.1 }}
                                                className="font-poppins text-charcoal-60 leading-relaxed"
                                            >
                                                {paragraph}
                                            </motion.p>
                                        ))}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Delivery Timeline */}
            <section id="delivery-timeline" className="section-spacing">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="max-w-4xl mx-auto"
                    >
                        <h2 className="font-playfair text-4xl text-deep-charcoal text-center mb-12">
                            Estimated Delivery Timeline
                        </h2>

                        <div className="relative">
                            <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-nude-beige -translate-y-1/2" />

                            <div className="flex justify-between relative z-10">
                                {timelineSteps.map((step, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, scale: 0 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: index * 0.2 }}
                                        className="flex flex-col items-center"
                                    >
                                        <div className="w-12 h-12 bg-white border-4 border-soft-ivory rounded-full
                      flex items-center justify-center text-2xl mb-2 shadow-soft">
                                            {step.icon}
                                        </div>
                                        <div className="text-center">
                                            <div className="font-poppins font-semibold text-deep-charcoal">
                                                {step.day}
                                            </div>
                                            <div className="font-poppins text-sm text-charcoal-60">
                                                {step.label}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Timeline Note */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="mt-12 text-center"
                        >
                            <p className="font-poppins text-sm text-charcoal-60">
                                *Timeline based on Standard Shipping within continental US. International orders may take additional time.
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="section-spacing bg-nude-beige/30">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="max-w-4xl mx-auto"
                    >
                        <h2 className="font-playfair text-4xl text-deep-charcoal text-center mb-12">
                            Shipping Questions?
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white p-6 rounded-2xl shadow-soft">
                                <h3 className="font-cormorant text-xl text-deep-charcoal mb-4">
                                    Live Chat
                                </h3>
                                <p className="font-poppins text-sm text-charcoal-60 mb-6">
                                    Available Monday-Friday, 9AM-6PM EST
                                </p>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full px-4 py-3 bg-deep-charcoal text-white text-sm font-poppins rounded-lg
                    hover:bg-deep-charcoal/90 transition-all duration-300"
                                >
                                    Start Chat
                                </motion.button>
                            </div>

                            <div className="bg-white p-6 rounded-2xl shadow-soft">
                                <h3 className="font-cormorant text-xl text-deep-charcoal mb-4">
                                    Email Support
                                </h3>
                                <p className="font-poppins text-sm text-charcoal-60 mb-6">
                                    Response within 24 hours
                                </p>
                                <motion.a
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    href="mailto:shipping@kromaic.com"
                                    className="w-full inline-block px-4 py-3 bg-white border border-nude-beige text-deep-charcoal
                    text-sm font-poppins rounded-lg hover:border-muted-sage hover:text-muted-sage
                    transition-all duration-300 text-center"
                                >
                                    shipping@kromaic.com
                                </motion.a>
                            </div>

                            <div className="bg-white p-6 rounded-2xl shadow-soft">
                                <h3 className="font-cormorant text-xl text-deep-charcoal mb-4">
                                    Phone Support
                                </h3>
                                <p className="font-poppins text-sm text-charcoal-60 mb-6">
                                    For urgent shipping inquiries
                                </p>
                                <motion.a
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    href="tel:+18885551234"
                                    className="w-full inline-block px-4 py-3 bg-muted-sage text-white text-sm font-poppins
                    rounded-lg hover:bg-muted-sage/90 transition-all duration-300 text-center"
                                >
                                    +1 (888) 555-1234
                                </motion.a>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* International Shipping Info */}
            <section id="international" className="section-spacing">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl mx-auto text-center"
                    >
                        <div className="bg-gradient-to-r from-muted-sage/5 to-champagne-gold/5 rounded-2xl p-8 md:p-12">
                            <h3 className="font-cormorant text-3xl text-deep-charcoal mb-6">
                                International Shipping Notice
                            </h3>
                            <p className="font-poppins text-charcoal-60 mb-8">
                                We proudly ship to over 50 countries worldwide. Please note that customs duties, taxes,
                                and fees are the responsibility of the recipient and are calculated at checkout.
                            </p>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {["🌍 Global Coverage", "📝 Duties Included", "🔄 Easy Returns", "📦 Safe Delivery"].map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                                        className="bg-white p-4 rounded-xl shadow-soft"
                                    >
                                        <p className="font-poppins text-sm text-charcoal-60">{item}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ShippingPolicy;